import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Event } from '../../models/event/event';
import { eventService } from '../../services/event-service';
import { NavigationUtil } from '../../utils/navigation-util';
import { DateUtil } from '../../utils/date-util';

interface MobileCalendarProps {
  events: Event[];
}

type MobileViewMode = 'day' | 'month';

export const MobileCalendar: React.FC<MobileCalendarProps> = ({ events }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isRTL = i18n.language === 'ar';

  const [viewMode, setViewMode] = useState<MobileViewMode>('month');
  const [currentDate, setCurrentDate] = useState(new Date());

  // Navigation functions - reusing existing logic
  const navigateDate = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);

    if (viewMode === 'day') {
      newDate.setDate(newDate.getDate() + (direction === 'next' ? 1 : -1));
    } else if (viewMode === 'month') {
      newDate.setMonth(newDate.getMonth() + (direction === 'next' ? 1 : -1));
    }

    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const handleEventClick = (event: Event) => {
    NavigationUtil.navigateToEvent(event, navigate);
  };

  // Reuse existing event filtering logic
  const getEventsForDate = (date: Date) => {
    return events.filter(event => {
      const eventStartDate = DateUtil.parseDateString(event.startDate);
      const eventEndDate = DateUtil.parseDateString(event.endDate);

      const checkDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const startDate = new Date(eventStartDate.getFullYear(), eventStartDate.getMonth(), eventStartDate.getDate());
      const endDate = new Date(eventEndDate.getFullYear(), eventEndDate.getMonth(), eventEndDate.getDate());

      return checkDate >= startDate && checkDate <= endDate;
    });
  };

  // Reuse existing multi-day event logic
  const isMultiDayEvent = (event: Event) => {
    const eventStartDate = DateUtil.parseDateString(event.startDate);
    const eventEndDate = DateUtil.parseDateString(event.endDate);
    const startDate = new Date(eventStartDate.getFullYear(), eventStartDate.getMonth(), eventStartDate.getDate());
    const endDate = new Date(eventEndDate.getFullYear(), eventEndDate.getMonth(), eventEndDate.getDate());
    return endDate.getTime() > startDate.getTime();
  };

  // Mobile-optimized day view inspired by iOS Calendar
  // Layout Decisions:
  // - Vertical scroll-friendly event list (instead of grid-based layout)
  // - Clean iOS-style day header with centered date display
  // - Touch-optimized event cards with clear visual hierarchy
  // - Single column layout prevents overlapping on small screens
  const renderMobileDayView = () => {
    const dayEvents = getEventsForDate(currentDate);

    return (
      <div className="bg-white">
        {/* Day Header - Clean iOS-style */}
        <div className="bg-white border-b border-gray-100 px-4 py-3">
          <div className="text-center">
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
              {currentDate.toLocaleDateString(i18n.language, { weekday: 'long' })}
            </div>
            <div className="text-2xl font-semibold text-black">
              {currentDate.getDate()}
            </div>
            <div className="text-sm text-gray-600">
              {currentDate.toLocaleDateString(i18n.language, { month: 'long', year: 'numeric' })}
            </div>
          </div>
        </div>

        {/* Events List - Vertical scroll-friendly layout */}
        <div className="px-4 py-2">
          {dayEvents.length > 0 ? (
            <div className="space-y-2">
              {/* Events count indicator */}
              <div className="text-center py-2">
                <span className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                  {dayEvents.length} {dayEvents.length === 1 ? t('pages.events.title').slice(0, -1) : t('pages.events.title')}
                </span>
              </div>

              {dayEvents.map(event => {
                const isOngoing = eventService.isEventOngoing(event);
                const isUpcoming = eventService.isEventUpcoming(event);
                const isMultiDay = isMultiDayEvent(event);

                return (
                  <div
                    key={event.id}
                    onClick={() => handleEventClick(event)}
                    className="bg-white border border-gray-200 rounded-xl p-4 active:bg-gray-50 transition-colors"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    {/* Event time and status row */}
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-2">
                        <div className="text-sm font-medium text-black">
                          {event.startTime} - {event.endTime}
                        </div>
                        {isMultiDay && (
                          <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded-full font-medium">
                            {DateUtil.parseDateString(event.startDate).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' })} - {DateUtil.parseDateString(event.endDate).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' })}
                          </span>
                        )}
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                        isOngoing
                          ? 'bg-green-100 text-green-800'
                          : isUpcoming
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-800'
                      }`}>
                        {isOngoing ? t('pages.events.status.ongoing') : isUpcoming ? t('pages.events.status.upcoming') : t('pages.events.status.completed')}
                      </span>
                    </div>

                    {/* Event title */}
                    <h3 className="text-base font-semibold text-black mb-1 leading-tight">
                      {event.title}
                    </h3>

                    {/* Event location */}
                    <div className="flex items-center text-sm text-gray-600 mb-2">
                      <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      </svg>
                      <span className="truncate">{event.address.name}</span>
                    </div>

                    {/* Event category */}
                    <div className="flex items-center justify-between">
                      <span className="text-xs bg-black text-white px-2 py-1 rounded-md font-medium">
                        {t(`pages.events.categories.${event.category}`)}
                      </span>
                      
                      {/* Chevron indicator */}
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            /* Empty state */
            <div className="text-center py-12">
              <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2-2v12a2 2 0 002 2z" />
              </svg>
              <h3 className="text-base font-medium text-gray-900 mb-1">{t('pages.events.noEvents')}</h3>
              <p className="text-sm text-gray-500">{t('pages.events.noEventsDescription')}</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Mobile-optimized month view - Compact calendar grid
  // Layout Decisions:
  // - Compact 7-column grid with touch-friendly cells (aspect-square)
  // - Event indicator dots instead of full event display to prevent clutter
  // - Tap-to-navigate-to-day functionality for better mobile UX
  // - Minimal text to maximize readability on small screens
  const renderMobileMonthView = () => {
    const currentMonth = currentDate.getMonth();
    const currentYear = currentDate.getFullYear();

    const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
    const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
    const firstDayOfWeek = firstDayOfMonth.getDay();
    const daysInMonth = lastDayOfMonth.getDate();

    // Generate calendar days
    const calendarDays: (Date | null)[] = [];

    // Add empty cells for days before the first day of month
    for (let i = 0; i < firstDayOfWeek; i++) {
      calendarDays.push(null);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      calendarDays.push(new Date(currentYear, currentMonth, day));
    }

    // Pad trailing empty cells so the grid always has complete weeks
    while (calendarDays.length % 7 !== 0) {
      calendarDays.push(null);
    }
    const dayNames = isRTL
      ? ['ح', 'ن', 'ث', 'ر', 'خ', 'ج', 'س']
      : ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

    return (
      <div className="bg-white">
        {/* Month header */}
        <div className="bg-gray-50 border-b border-gray-200">
          <div className="grid grid-cols-7">
            {dayNames.map((day, index) => (
              <div key={index} className="p-2 text-center">
                <div className="text-xs font-medium text-gray-600">
                  {day}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Month grid - Optimized for mobile tapping */}
        <div className="grid grid-cols-7 border-b border-gray-200">
          {calendarDays.map((date, index) => {
            const dayEvents = date ? getEventsForDate(date) : [];
            const isToday = date &&
              date.getDate() === new Date().getDate() &&
              date.getMonth() === new Date().getMonth() &&
              date.getFullYear() === new Date().getFullYear();
            
            const hasEvents = dayEvents.length > 0;

            return (
              <div
                key={index}
                onClick={() => {
                  if (date) {
                    setCurrentDate(date);
                    setViewMode('day');
                  }
                }}
                className={`aspect-square p-1 border-r border-b border-gray-100 last:border-r-0 ${
                  date 
                    ? `cursor-pointer active:bg-gray-100 transition-colors ${hasEvents ? 'bg-blue-50' : 'bg-white'}` 
                    : 'bg-gray-25'
                }`}
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                {date && (
                  <div className="h-full flex flex-col">
                    {/* Date number */}
                    <div className={`text-sm font-medium text-center ${
                      isToday
                        ? 'bg-black text-white rounded-full w-6 h-6 flex items-center justify-center mx-auto'
                        : 'text-gray-900 p-1'
                    }`}>
                      {date.getDate()}
                    </div>

                    {/* Event indicator dots */}
                    {hasEvents && (
                      <div className="flex justify-center mt-1">
                        <div className={`flex space-x-1 ${dayEvents.length > 3 ? 'flex-wrap justify-center' : ''}`}>
                          {dayEvents.slice(0, 3).map((event, idx) => (
                            <div
                              key={idx}
                              className="w-1.5 h-1.5 rounded-full bg-black"
                              title={event.title}
                            />
                          ))}
                          {dayEvents.length > 3 && (
                            <div className="text-xs text-gray-600 font-medium">
                              +{dayEvents.length - 3}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Quick event preview for selected day - Shows when day has events */}
        {viewMode === 'month' && (
          <div className="bg-gray-50 border-t border-gray-200 p-3">
            <div className="text-xs text-gray-600 text-center">
              {t('mobileCalendar.tapDayToViewEvents')}
            </div>
          </div>
        )}
      </div>
    );
  };

  const getDateDisplayText = () => {
    if (viewMode === 'day') {
      return currentDate.toLocaleDateString(i18n.language, {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } else {
      return currentDate.toLocaleDateString(i18n.language, {
        year: 'numeric',
        month: 'long'
      });
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
      {/* Mobile Calendar Header - Compact and touch-friendly 
          Layout Decisions:
          - Reduced to 2 view modes (day/month) for simplicity on small screens
          - Touch-optimized button sizes with proper tap targets (44px minimum)
          - WebkitTapHighlightColor removed for cleaner iOS-like experience
          - Compact navigation controls to maximize content space */}
      <div className="bg-black text-white">
        <div className="px-4 py-3">
          {/* Navigation Row */}
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={() => navigateDate('prev')}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors active:bg-white/30"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <div className="text-center flex-1 px-2">
              <h3 className="text-lg font-semibold truncate">
                {viewMode === 'day' 
                  ? currentDate.toLocaleDateString(i18n.language, { month: 'long', year: 'numeric' })
                  : getDateDisplayText()
                }
              </h3>
            </div>

            <button
              onClick={() => navigateDate('next')}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors active:bg-white/30"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* View Mode Toggle and Today Button */}
          <div className="flex items-center justify-between">
            {/* View Mode Toggle */}
            <div className="flex bg-white/10 rounded-lg p-1">
              {(['day', 'month'] as MobileViewMode[]).map((mode) => (
                <button
                  key={mode}
                  onClick={() => setViewMode(mode)}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-all ${
                    viewMode === mode
                      ? 'bg-white text-black shadow-sm'
                      : 'text-white/80 hover:text-white hover:bg-white/20'
                  }`}
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  {t(`view.${mode}`)}
                </button>
              ))}
            </div>

            {/* Today Button */}
            <button
              onClick={goToToday}
              className="px-3 py-2 text-sm font-medium bg-white text-black rounded-lg hover:bg-gray-100 transition-colors active:bg-gray-200"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              {t('navigation.today')}
            </button>
          </div>
        </div>
      </div>

      {/* Calendar Content */}
      {viewMode === 'day' && renderMobileDayView()}
      {viewMode === 'month' && renderMobileMonthView()}
    </div>
  );
};